// ENT Surgery FAQs — grouped for the dedicated FAQs page (20 detailed FAQs).

export const FAQ_GROUPS = [
  {
    category: "Deciding on Surgery",
    faqs: [
      {
        q: "How do I know if I actually need ENT surgery?",
        a: "Surgery is recommended only when symptoms are significant and do not respond to medication, or when there is a structural problem that cannot be corrected otherwise. Dr. Sanjay Teza follows an evidence-based approach, advising surgery only when it clearly benefits your health and quality of life.",
      },
      {
        q: "Can I try medical treatment before considering surgery?",
        a: "In most conditions, yes. Medication, lifestyle measures and conservative treatment are usually tried first. Surgery is considered when these do not give lasting relief or when the condition is likely to worsen without it.",
      },
      {
        q: "Will I be told about non-surgical options too?",
        a: "Absolutely. Every consultation includes a transparent discussion of all suitable options, their benefits and limitations, so you can make an informed decision.",
      },
      {
        q: "Are these surgeries suitable for elderly patients?",
        a: "Age alone is not a barrier. Fitness for surgery is assessed individually with pre-operative evaluation to ensure the procedure is safe for you.",
      },
      {
        q: "How do I get a second opinion before deciding?",
        a: "You are always welcome to seek a second opinion. Dr. Teza encourages informed decisions and will happily explain your reports, diagnosis and options so you feel confident about your choice.",
      },
      {
        q: "What happens if I delay a recommended surgery?",
        a: "Some conditions remain stable, while others, such as cholesteatoma or a growing nodule, can worsen or cause complications if delayed. Dr. Teza will clearly explain the risks of waiting in your specific case.",
      },
    ],
  },
  {
    category: "Safety & Anaesthesia",
    faqs: [
      {
        q: "Are ENT surgeries safe?",
        a: "Modern ENT surgeries are very safe when performed by an experienced surgeon using advanced equipment. Dr. Teza operates in fully modular OTs with Carl Zeiss microscopes and Stryker endoscopy for maximum precision and safety.",
      },
      {
        q: "What type of anaesthesia is used?",
        a: "It depends on the procedure. Some are done under local anaesthesia, while others require general anaesthesia. The safest suitable option is chosen and explained to you in advance.",
      },
      {
        q: "Is general anaesthesia risky?",
        a: "For most healthy patients, modern general anaesthesia is very safe. A pre-anaesthetic check-up is done beforehand to identify and manage any individual risk factors.",
      },
      {
        q: "What are the possible risks or complications?",
        a: "Every surgery carries small risks such as bleeding, infection or reaction to anaesthesia. These are uncommon with careful technique and proper aftercare, and all relevant risks are explained clearly before surgery.",
      },
      {
        q: "Will I feel any pain during the surgery?",
        a: "No. With local or general anaesthesia you will not feel pain during the procedure. Any discomfort afterwards is mild and well controlled with prescribed medication.",
      },
      {
        q: "How safe is the operation theatre and equipment?",
        a: "Surgery is performed in modular operation theatres with strict sterilisation and infection-control protocols, supported by well-maintained Carl Zeiss microscopes and Stryker endoscopy systems.",
      },
    ],
  },
  {
    category: "Hospitalisation & The Procedure",
    faqs: [
      {
        q: "Will I need to be admitted to hospital?",
        a: "Many ENT surgeries are day-care procedures, meaning you can go home the same day. Some, such as mastoidectomy, may need a short hospital stay. This is discussed with you beforehand.",
      },
      {
        q: "How long does the surgery take?",
        a: "Most ENT surgeries take between 30 minutes and 2 hours, depending on the complexity of the procedure and your specific condition.",
      },
      {
        q: "Will there be visible scars?",
        a: "Many ENT surgeries are performed endoscopically or through the mouth or ear canal, leaving no external scars. Where an incision is needed, it is placed discreetly to minimise visibility.",
      },
      {
        q: "Can more than one problem be treated in a single surgery?",
        a: "Often yes. For example, septoplasty and FESS, or tonsillectomy and adenoidectomy, can be combined in one sitting when appropriate, reducing the need for repeat anaesthesia.",
      },
      {
        q: "Do I need to stop my regular medicines before surgery?",
        a: "Most medicines are continued, but blood thinners and certain drugs may need to be adjusted. Always share your full medication list so clear pre-operative instructions can be given.",
      },
      {
        q: "How long before surgery should I stop eating and drinking?",
        a: "For procedures under general anaesthesia, you are usually asked to fast for 6–8 hours beforehand. Exact timing will be confirmed by the team before your surgery date.",
      },
    ],
  },
  {
    category: "Recovery & Return to Life",
    faqs: [
      {
        q: "How long is the recovery period?",
        a: "Recovery varies by procedure. Many patients return to routine activities within 5–10 days, while complete internal healing may take a few weeks. You will receive a clear recovery plan for your specific surgery.",
      },
      {
        q: "When can I return to work or school?",
        a: "For most day-care ENT surgeries, patients resume light work within a week. Physically demanding jobs may need a slightly longer break, which will be advised individually.",
      },
      {
        q: "Are there any activity restrictions after surgery?",
        a: "Yes, temporarily. Heavy lifting, strenuous exercise, swimming and, for some ear surgeries, air travel may be restricted for a few weeks to protect healing.",
      },
      {
        q: "What follow-up will I need?",
        a: "Follow-up visits are scheduled to monitor healing, remove any packing or dressings, and confirm a good outcome. Attending these is important for the best long-term result.",
      },
      {
        q: "Is some pain or discharge normal after surgery?",
        a: "Yes. Mild pain, slight bleeding or discharge, and congestion are common in the first days and settle gradually. You will be told which symptoms are normal and which need attention.",
      },
      {
        q: "When can I resume exercise and gym workouts?",
        a: "Light activity resumes within days, but strenuous exercise, gym and heavy lifting are usually avoided for 2–3 weeks to protect healing. Follow the specific advice for your surgery.",
      },
    ],
  },
  {
    category: "Costs & Common Myths",
    faqs: [
      {
        q: "Is ENT surgery very expensive?",
        a: "Cost depends on the specific procedure and setting. Dr. Teza's practice believes in transparent, honest pricing with a clear explanation of what a procedure involves before you decide.",
      },
      {
        q: "Myth: ENT surgery always causes permanent hearing or voice damage.",
        a: "This is untrue. Modern microscopic and endoscopic techniques are designed to preserve and often improve hearing and voice. Vital nerves and structures are carefully protected.",
      },
      {
        q: "Myth: Once you have surgery, the problem always comes back.",
        a: "Most well-indicated ENT surgeries provide long-lasting relief. Recurrence is uncommon when surgery is done correctly and aftercare instructions are followed.",
      },
      {
        q: "Myth: Sinus and nasal surgeries change the shape of your face or nose.",
        a: "Functional sinus and septal surgeries work internally and do not alter your facial appearance. The external nose shape remains unchanged.",
      },
      {
        q: "Is health insurance usually accepted for ENT surgery?",
        a: "Many ENT surgeries are covered by health insurance. Our team can guide you on the paperwork and pre-authorisation process so the financial side is as smooth and transparent as possible.",
      },
    ],
  },
  {
    category: "Children's ENT Surgery",
    faqs: [
      {
        q: "Are ENT surgeries safe for young children?",
        a: "Yes. Procedures like adenoidectomy, tonsillectomy and grommet insertion are among the most common and safest paediatric surgeries when properly indicated, and are performed with child-friendly, careful anaesthesia.",
      },
      {
        q: "How do I prepare my child for surgery?",
        a: "Keep the explanation simple and reassuring, follow the fasting instructions carefully, and bring a comfort item. Our team helps keep children calm before and after the procedure.",
      },
      {
        q: "Will my child's speech or development be affected?",
        a: "Correctly indicated surgeries usually improve breathing, hearing and sleep, which support healthy speech and development. They do not harm your child's growth.",
      },
      {
        q: "How soon can my child return to school?",
        a: "For most paediatric ENT surgeries, children return to school within 3–7 days, depending on the procedure and how they feel. Specific guidance is given at discharge.",
      },
    ],
  },
];
